import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import Cookies from 'js-cookie';
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import { nanoid } from "nanoid";
import { LoadingView } from 'day8-react';
import { loadReCaptcha } from 'react-recaptcha-google';
import { MediaContextProvider } from 'day8-react/build/new-components';
import { Toaster, CurrencyProvider } from 'day8-ui';

import configureStore from './store/configureStore';
import Routing from './routing/Routing';
import { fetchUser } from './actions/user';
import { SortByModalProvider } from './context/SortByModalContext';
import { NavbarProvider } from './context/NavbarContext';
import { TooltipModalProvider } from './context/TooltipModalContext';
import TooltipModal from './components/TooltipModal';
import { YachtModalProvider, useYachtModal } from './context/YachtModalContext';
import SanitisedYachtModal from './components/booking/SanitisedYachtModal';
import { FilterModalProvider } from './context/FilterModalContext';
import { ActiveCompanyProvider, useActiveCompany } from './context/ActiveCompanyContext';
import { onMarketingConsent } from './helpers/consent';
import { loadIntercom } from './helpers/intercom';
import { COMPANIES } from './constants';
import history from './history';

const YachtModalRenderer = () => {
  const { isModalOpen } = useYachtModal();
  return isModalOpen ? <SanitisedYachtModal /> : null;
};

let gb_custom_visitor_id = localStorage.getItem("gb_custom_visitor_id");
if (!gb_custom_visitor_id) {
  gb_custom_visitor_id = nanoid();
  localStorage.setItem("gb_custom_visitor_id", gb_custom_visitor_id);
}

export const growthbook = new GrowthBook({
  apiHost: process.env.GROWTHBOOK_API_HOST,
  clientKey: process.env.GROWTHBOOK_CLIENT_KEY,
  enableDevMode: true,
  trackingCallback: (experiment, result) => {
    window?.dataLayer?.push({
      event: 'experiment_viewed',
      experiment_id: experiment.key,
      variation_id: result.key,
      gb_custom_visitor_id
    });
  }
});

// Supplies the active brand to the shared CurrencyProvider, which is brand-agnostic by design -
// it lives in day8-ui and is used by products that run a single brand.
//
// The two brands are separate merchant accounts, so their FX quotes can carry different pricing and
// a payment is charged against its own brand's rate. Showing a YNF guest the TYW rate would
// disagree with what they are charged. /api/fxrates serves every brand in one response, so this
// only selects; changing brand mid-session costs no request.
const BrandedCurrencyProvider = ({ children }) => {
  const { isYachtsAndFriends } = useActiveCompany();

  return (
    <CurrencyProvider companyId={isYachtsAndFriends ? COMPANIES.YNF : COMPANIES.DAY8}>
      {children}
    </CurrencyProvider>
  );
};

BrandedCurrencyProvider.propTypes = {
  children: PropTypes.any
};

// Component to update data-theme attribute based on context
const ThemeUpdater = ({ rootRef }) => {
  const { isYachtsAndFriends } = useActiveCompany();

  React.useEffect(() => {
    if (rootRef.current) {
      rootRef.current.setAttribute('data-theme', isYachtsAndFriends ? 'ynf' : 'default');
    }
  }, [isYachtsAndFriends, rootRef]);

  return null;
};

ThemeUpdater.propTypes = {
  rootRef: PropTypes.object
};

class Root extends PureComponent {
  constructor() {
    super();
    this.state = {
      storeLoaded: false,
      store: null
    };
    this.rootRef = React.createRef();
  }

  async componentDidMount() {
    // Intercom sets cookies, so it waits for marketing consent. Cookiebot only
    // exists where GTM runs, so in development there is nothing to wait for.
    const intercomAppId = process.env.INTERCOM_APP_ID;
    if (intercomAppId) {
      if (process.env.NODE_ENV === 'development') {
        loadIntercom(intercomAppId);
      } else {
        this.detachIntercomConsent = onMarketingConsent(() => loadIntercom(intercomAppId));
      }
    }

    // Initialize store
    try {
      const store = await configureStore();
      await store.dispatch(fetchUser());
      this.setState({
        store,
        storeLoaded: true
      });
    } catch (error) {
      console.error('Failed to initialize store:', error);
      // Handle store initialization error
    }

    // Initialize other services
    growthbook.init({ streaming: true });
    loadReCaptcha();
  }

  componentWillUnmount() {
    if (this.detachIntercomConsent) this.detachIntercomConsent();
  }

  componentDidUpdate(_, prevState) {
    if (history.location.pathname !== this.lastPathname) {
      this.lastPathname = history.location.pathname;

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      growthbook.setURL(window.location.href);
      growthbook.setAttributes({
        id: gb_custom_visitor_id,
        user_id: user?.id,
        query: window.location.search,
        host: window.location.host,
        loggedIn: !!user,
        employee: user?.email?.endsWith("@day8.com"),
        deviceType: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        browser: navigator.userAgent,
        url: history.location.pathname,
        utmSource: Cookies.get('utm_source'),
        utmMedium: Cookies.get('utm_medium'),
        utmCampaign: Cookies.get('utm_campaign'),
        utmContent: Cookies.get('utm_content'),
        utmTerm: Cookies.get('utm_term')
      });
    }
  }

  render() {
    const { storeLoaded, store } = this.state;

    if (!storeLoaded) {
      return <LoadingView />;
    }

    return (
      <div ref={this.rootRef} style={{ minHeight: '100vh' }} data-theme="default">
        <Provider store={store}>
          <ActiveCompanyProvider>
            <ThemeUpdater rootRef={this.rootRef} />
            <GrowthBookProvider growthbook={growthbook}>
              <CookiesProvider>
                <BrandedCurrencyProvider>
                  <MediaContextProvider>
                    <FilterModalProvider>
                      <SortByModalProvider>
                        <NavbarProvider>
                          <YachtModalProvider>
                            <TooltipModalProvider>
                              <Routing />
                              <Toaster position="top-right" style={{ marginTop: '64px' }} />
                              <TooltipModal />
                              <YachtModalRenderer />
                            </TooltipModalProvider>
                          </YachtModalProvider>
                        </NavbarProvider>
                      </SortByModalProvider>
                    </FilterModalProvider>
                  </MediaContextProvider>
                </BrandedCurrencyProvider>
              </CookiesProvider>
            </GrowthBookProvider>
          </ActiveCompanyProvider>
        </Provider>
      </div>
    );
  }
}

export default Root;
